import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Logger } from './logger.service';
import { findIndex, filter } from 'lodash';

const log = new Logger('GlobalNotifierService');
type Event = 'none' | 'click' | 'change';

/**
 * @description:
 * source: nombre desde donde se realiza la notificación.
 * eventTriggered: evento que se utilizo para desencadenar la notificación.
 * data: datos que se quieren notificar.
 *
 * @example:
 * {
 *  source: 'task';
 *  eventTriggered: 'click';
 *  data: 'Datos de la notificación';
 * }
 *
 * @interface Notification
 */
interface GlobalNotification<T> {
  index?: number;
  source: string;
  destiny: string;
  eventTriggered: Event;
  action: string;
  data?: T;
}

/**
 * Posibles parametros de busqueda para consultar las
 * notificaciones con un determinado query.
 *
 * @interface ParamsFilter
 */
interface ParamsFilter {
  source: string;
  destiny: string;
  eventTriggered: Event;
  action: string;
}


@Injectable()
export class GlobalNotifierService {
  // Notificaciones en espera a ser entregadas
  private notifications: GlobalNotification<any>[] = [];
  // Notifica las notificaciones entrentes
  private subject = new Subject<GlobalNotification<any>>();

  /**
   * Genera una nueva notificación la cual es avisada
   * a los suscriptores y puesta en espera en caso de
   * que el suscriptor interesado no pueda atender la
   * notificación una vez generada.
   *
   * @template T
   * @param {GlobalNotification<T>} notification
   * @param {boolean} [isUnique=false] - En true solo se almacena esta notificación una sola vez.
   * @memberof GlobalNotifierService
   */
  set<T>(notification: GlobalNotification<T>, isUnique: boolean = false) {
    const push = () => {
      this.notifications.push(notification);
      notification.index = this.notifications.length - 1;
    };
    if (isUnique) {
      const index = findIndex(this.notifications, {
        source: notification.source,
        destiny: notification.destiny,
        action: notification.action
      });
      if (index !== -1) {
        notification.index = index;
        this.notifications[index] = notification;
      } else {
        push();
      }
    } else {
      push();
    }
    this.subject.next(notification);
  }

  /**
   * Elimina una notificación usando su indice en el array.
   *
   * @param {number} index
   * @memberof GlobalNotifierService
   */
  delete(index: number) {
    this.notifications.splice(index, 1);
    this.loadIndex()
  }

  /**
   * Retorna todas las notificaciones que coincidan con un determinado query.
   *
   * @param {Partial<ParamsFilter>} query
   * @returns {GlobalNotification<any>[]}
   * @memberof GlobalNotifierService
   */
  get(query: Partial<ParamsFilter>): GlobalNotification<any>[] {
    return filter(this.notifications, query);
  }

  /**
   * Genera un canal para escuchar las notificaciones que se emiten en tiempo real.
   *
   * @returns {Observable<GlobalNotification<any>>}
   * @memberof GlobalNotifierService
   */
  listen(): Observable<GlobalNotification<any>> {
    return this.subject.asObservable();
  }

  /**
   * Actualiza el index de las notificaciones
   *
   * @private
   * @memberof GlobalNotifierService
   */
  private loadIndex() {
    this.notifications.forEach((element, i) => {
      element.index = i;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/core/utilities/global-notifier.service.ts